import React, { useRef, useState } from "react";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl } from "../../lib/api";

export const ImageUpload = ({ value, onChange, testid }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error("Upload failed — use PNG, JPG, WEBP, GIF or SVG");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      {value ? (
        <div className="relative w-16 h-16 rounded-xl overflow-hidden border border-plum-deep/15 bg-cream shrink-0">
          <img src={imgUrl(value)} alt="" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-0.5 right-0.5 bg-plum-deep/70 text-cream rounded-full p-0.5"
            data-testid={testid ? `${testid}-clear` : undefined}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <div className="w-16 h-16 rounded-xl border border-dashed border-plum-deep/25 bg-cream shrink-0" />
      )}
      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        data-testid={testid ? `${testid}-btn` : undefined}
        className="inline-flex items-center gap-2 rounded-full bg-blush text-plum-deep px-4 py-2 text-xs uppercase tracking-[0.14em] font-semibold hover:bg-magenta hover:text-cream transition-colors disabled:opacity-50"
      >
        <Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Upload"}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
      />
    </div>
  );
};

export const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.2em] text-plum-deep/60 mb-1.5 font-semibold">{label}</span>
    {children}
  </label>
);

export const TextInput = (props) => (
  <input {...props} className={`w-full rounded-full px-5 py-2.5 bg-cream border border-plum-deep/15 text-sm focus:outline-none focus:border-magenta ${props.className || ""}`} />
);

export const Toggle = ({ checked, onChange, testid }) => (
  <button
    onClick={onChange}
    data-testid={testid}
    className={`relative w-11 h-6 rounded-full transition-colors ${checked ? "bg-forest" : "bg-plum-deep/20"}`}
    aria-label="Toggle active"
  >
    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-cream transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
  </button>
);

export const TextArea = (props) => (
  <textarea {...props} className={`w-full rounded-2xl px-5 py-3 bg-cream border border-plum-deep/15 text-sm focus:outline-none focus:border-magenta resize-none ${props.className || ""}`} />
);

export const PageTitle = ({ kicker, title, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
    <div>
      <span className="uppercase tracking-[0.3em] text-magenta text-[11px] font-semibold">{kicker}</span>
      <h1 className="font-serif-display uppercase text-plum-deep text-3xl md:text-4xl mt-1">{title}</h1>
    </div>
    {children}
  </div>
);

export const STATUS_OPTIONS = [
  { value: "draft", label: "DRAFT QUOTE", color: "#C39EE3" },
  { value: "awaiting_payment", label: "AWAITING PAYMENT", color: "#C61F62" },
  { value: "new", label: "NEW", color: "#EC2675" },
  { value: "in_production", label: "IN PRODUCTION", color: "#FF7A3D" },
  { value: "completed", label: "COMPLETED", color: "#1F8A50" },
  { value: "shipped", label: "SHIPPED", color: "#4ECDE9" },
  { value: "cancelled", label: "CANCELLED", color: "#6E2470" },
];

export const StatusBadge = ({ status }) => {
  const opt = STATUS_OPTIONS.find((s) => s.value === status) || { label: status, color: "#6E2470" };
  return (
    <span className="inline-block px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.14em] font-bold text-cream" style={{ background: opt.color }}>
      {opt.label}
    </span>
  );
};
